/**
 * ZendeskTickets.js — Zendesk ticket import, browsing, and testing component.
 * v1.4 — KB-grounded simulation: RunTestModal, Groundedness metric, hallucination detection.
 */

import React, { useState, useEffect } from 'react';
import { useApi } from './hooks/useApi';
import { PieChart, Pie, Cell, RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { Download } from 'lucide-react';
import './ZendeskTickets.css';
import RunTestModal from './RunTestModal';
import './RunTestModal.css';

const API_BASE = `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/zendesk`;

const ZendeskTickets = ({ theme, sharedKbArticles = null, sharedAgentPrompt = '', onUpgradeNeeded, onRegisterConfigureImport }) => {
  const { authFetch } = useApi();
  const [integrationStatus, setIntegrationStatus] = useState(null);
  const [oauthStatus, setOauthStatus] = useState(null);
  const [subdomain, setSubdomain] = useState('');
  const [connecting, setConnecting] = useState(false);

  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);

  const [filterPriority, setFilterPriority] = useState('all');
  const [filterStatus,   setFilterStatus]   = useState('all');
  const [filterChannel,  setFilterChannel]  = useState('all');
  const [selectedTags,   setSelectedTags]   = useState(new Set());

  const [testingTicket, setTestingTicket] = useState(null);
  const [testProgress, setTestProgress] = useState(0);
  const [testResults, setTestResults] = useState(null);

  const [ticketResults, setTicketResults] = useState({});
  const [expandedTickets, setExpandedTickets] = useState({});

  const [batchTesting, setBatchTesting] = useState(false);
  const [batchProgress, setBatchProgress] = useState(0);
  const [batchProgressMessage, setBatchProgressMessage] = useState('');
  const [batchResults, setBatchResults] = useState(null);

  // ── RunTestModal state ────────────────────────────────────────────
  const [runTestModalOpen, setRunTestModalOpen] = useState(false);
  const [runTestModalMode, setRunTestModalMode] = useState('single'); // 'single' | 'batch'
  const [pendingTestTicketId, setPendingTestTicketId] = useState(null);

  // ── Import settings ───────────────────────────────────────────────
  const [importDaysBack, setImportDaysBack] = useState(30);
  const [importMaxTickets, setImportMaxTickets] = useState(50);
  const [importPriority, setImportPriority] = useState('');
  const [importStatus, setImportStatus] = useState('');
  const [importChannel, setImportChannel] = useState('');
  const [importStartDate, setImportStartDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [importEndDate, setImportEndDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [importMinTicketId, setImportMinTicketId] = useState('');
  const [importMaxTicketId, setImportMaxTicketId] = useState('');
  const [importIncludeComments, setImportIncludeComments] = useState(true);
  const [importFilterMode, setImportFilterMode] = useState('date'); // 'date' | 'id'
  const [lastImportInfo, setLastImportInfo] = useState(null);

  const [showImportFilters, setShowImportFilters] = useState(true);   // expanded by default
  const [showClearConfirm, setShowClearConfirm]  = useState(false);

  // ── Config modal ──────────────────────────────────────────────────
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [configStep, setConfigStep] = useState(1); // 1 = instructions, 2 = credentials
  const [configSubdomain, setConfigSubdomain] = useState('');
  const [configClientId, setConfigClientId] = useState('');
  const [configClientSecret, setConfigClientSecret] = useState('');
  const [configSaving, setConfigSaving] = useState(false);
  const [configError, setConfigError] = useState('');

  const MAX_TICKETS_LIMIT = 1000;

  useEffect(() => {
    // Handle Zendesk OAuth callback result in URL params
    const params = new URLSearchParams(window.location.search);
    const zendeskParam = params.get('zendesk');
    if (zendeskParam === 'connected') {
      const subdomain = params.get('subdomain');
      // Clean the URL without reloading
      window.history.replaceState({}, document.title, window.location.pathname);
      // Show success — status checks below will update UI
    } else if (zendeskParam === 'error') {
      const reason = params.get('reason') || 'unknown_error';
      window.history.replaceState({}, document.title, window.location.pathname);
      const messages = {
        invalid_state: 'The connection attempt expired or was invalid. Please try connecting again.',
        token_exchange_failed: 'Could not exchange the authorization code for a token. Please verify your Client ID and Client Secret are correct and try again.',
        token_verification_failed: 'The token was obtained but could not be verified with Zendesk. Please try again.',
      };
      alert(`⚠️ Zendesk connection failed\n\n${messages[reason] || 'An unexpected error occurred. Please try again or contact support.'}`);
    }

    checkIntegrationStatus();
    checkOAuthStatus();
    fetchTickets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Register configure-import trigger so App.js header button can open the panel
  useEffect(() => {
    if (!onRegisterConfigureImport) return;
    onRegisterConfigureImport(() => {
      setShowImportFilters(true);
      setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 80);
    });
  }, [onRegisterConfigureImport]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchTickets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterPriority, filterStatus]);

  const checkIntegrationStatus = async () => {
    try {
      const data = await authFetch(`/api/zendesk/health`);
      setIntegrationStatus(data);
    } catch {
      setIntegrationStatus({ status: 'error', mode: 'unknown' });
    }
  };

  const checkOAuthStatus = async () => {
    try {
      const data = await authFetch(`/api/zendesk/oauth/status`);
      setOauthStatus(data);
    } catch {}
  };

  const saveZendeskCredentials = async () => {
    if (!configSubdomain.trim()) { setConfigError('Zendesk subdomain is required.'); return; }
    if (!configClientId.trim())  { setConfigError('Client ID is required.'); return; }
    if (!configClientSecret.trim()) { setConfigError('Client Secret is required.'); return; }
    setConfigSaving(true);
    setConfigError('');
    try {
      const data = await authFetch(`/api/zendesk/oauth/connect-custom`, {
        method: 'POST',
        body: JSON.stringify({
          subdomain:     configSubdomain.trim(),
          client_id:    configClientId.trim(),
          client_secret: configClientSecret.trim(),
        }),
      });
      setShowConfigModal(false);
      window.location.href = data.authorization_url;
    } catch (error) {
      setConfigError(error.message || 'Could not reach the backend. Is it running?');
    } finally {
      setConfigSaving(false);
    }
  };

  const connectZendesk = async () => {
    if (!subdomain.trim()) { alert('Please enter your Zendesk subdomain'); return; }
    setConnecting(true);
    try {
      const data = await authFetch(`/api/zendesk/oauth/connect`, {
        method: 'POST',
        body: JSON.stringify({ subdomain: subdomain.trim(), user_id: 'default_user' }),
      });
      window.location.href = data.authorization_url;
    } catch (error) {
      alert(`Connection failed: ${error.message}`);
      setConnecting(false);
    }
  };

  const disconnectZendesk = async (connectionId) => {
    if (!window.confirm('Disconnect this Zendesk account? Imported tickets will be kept.')) return;
    try {
      await authFetch(`/api/zendesk/oauth/disconnect/${connectionId}`, { method: 'DELETE' });
      await checkOAuthStatus();
    } catch {}
  };

  const fetchTickets = async () => {
    setLoading(true);
    try {
      const priorityParam = filterPriority !== 'all' ? `&priority=${filterPriority}` : '';
      const statusParam   = filterStatus   !== 'all' ? `&status=${filterStatus}`     : '';
      const data = await authFetch(`/api/zendesk/tickets?limit=200${priorityParam}${statusParam}`);
      setTickets(data.tickets || []);
    } catch {}
    finally { setLoading(false); }
  };

  const importMockTickets = async (count = 10) => {
    setImporting(true);
    try {
      const data = await authFetch(`/api/zendesk/import-mock-tickets`, {
        method: 'POST',
        body: JSON.stringify({ count }),
      });
      setLastImportInfo({ count: data.tickets_imported, source: 'mock', pii: 0, time: new Date().toLocaleTimeString() });
      fetchTickets();
      setTimeout(() => setLastImportInfo(null), 8000);
    } catch (error) {
      if (error.status === 402) {
        onUpgradeNeeded && onUpgradeNeeded();
      } else {
        alert('Failed to import mock tickets');
      }
    } finally {
      setImporting(false);
    }
  };

  const importRealTickets = async () => {
    const safeMax = Math.min(parseInt(importMaxTickets) || 50, MAX_TICKETS_LIMIT);
    setImporting(true);
    try {
      const payload = {
        max_tickets: safeMax,
        include_comments: importIncludeComments,
      };
      if (importFilterMode === 'id') {
        if (importMinTicketId) payload.min_ticket_id = parseInt(importMinTicketId);
        if (importMaxTicketId) payload.max_ticket_id = parseInt(importMaxTicketId);
        payload.days_back = 365;
      } else {
        if (importStartDate) {
          payload.start_date = importStartDate;
        } else {
          payload.days_back = importDaysBack;
        }
        if (importEndDate) payload.end_date = importEndDate;
      }
      if (importPriority) payload.priority = importPriority;
      if (importStatus)   payload.status   = importStatus;
      if (importChannel)  payload.channel  = importChannel;

      const data = await authFetch(`/api/zendesk/import-tickets`, {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      setLastImportInfo({
        count: data.tickets_imported,
        source: data.source,
        pii: data.pii_detections_total,
        time: new Date().toLocaleTimeString(),
      });
      fetchTickets();
      setTimeout(() => setLastImportInfo(null), 8000);
    } catch (error) {
      if (error.status === 402) {
        onUpgradeNeeded && onUpgradeNeeded();
      } else {
        alert(`Import failed: ${error.message}`);
      }
    } finally {
      setImporting(false);
    }
  };

  const clearAllTickets = async () => {
    try { await authFetch(`/api/zendesk/tickets/clear`, { method: 'DELETE' }); } catch {}
    setTickets([]);
    setTicketResults({});
    setExpandedTickets({});
    setBatchResults(null);
    setLastImportInfo(null);
    setShowClearConfirm(false);
  };

  // ── Single ticket test ────────────────────────────────────────────
  // Uses shared KB config from App.js if set; otherwise opens modal.

  const testSingleTicket = (ticketId) => {
    if (sharedKbArticles !== null || sharedAgentPrompt) {
      executeTicketTest(ticketId, {
        kb_articles: sharedKbArticles,
        agent_system_prompt: sharedAgentPrompt || null,
      });
      return;
    }
    setPendingTestTicketId(ticketId);
    setRunTestModalMode('single');
    setRunTestModalOpen(true);
  };

  const executeTicketTest = async (ticketId, { kb_articles, agent_system_prompt }) => {
    setTestingTicket(ticketId);
    setTestProgress(0);
    setTestResults(null);

    const progressInterval = setInterval(() => {
      setTestProgress(prev => prev >= 90 ? prev : prev + Math.random() * 15);
    }, 500);

    try {
      const data = await authFetch(`/api/zendesk/tickets/${ticketId}/test`, {
        method: 'POST',
        body: JSON.stringify({ kb_articles, agent_system_prompt }),
      });

      clearInterval(progressInterval);
      setTestProgress(100);

      setTimeout(() => {
        setTestResults(data);
        setTestProgress(0);
        if (data.status === 'success') {
          setTicketResults(prev => ({ ...prev, [ticketId]: data }));
          setExpandedTickets(prev => ({ ...prev, [ticketId]: true }));
        }
      }, 300);
    } catch {
      alert('Failed to test ticket');
      clearInterval(progressInterval);
      setTestProgress(0);
    } finally {
      setTimeout(() => setTestingTicket(null), 300);
    }
  };

  // ── Batch test ───────────────────────────────────────────────────
  // Uses shared KB config from App.js if set; otherwise opens modal.

  const runBatchTest = () => {
    if (sharedKbArticles !== null || sharedAgentPrompt) {
      executeBatchTest({
        kb_articles: sharedKbArticles,
        agent_system_prompt: sharedAgentPrompt || null,
      });
      return;
    }
    setRunTestModalMode('batch');
    setRunTestModalOpen(true);
  };

  const executeBatchTest = async ({ kb_articles, agent_system_prompt }) => {
    const testCount = tickets.length;
    setBatchTesting(true);
    setBatchProgress(0);
    setBatchProgressMessage('Initializing batch test...');
    setBatchResults(null);

    const startTime = Date.now();
    const progressInterval = setInterval(() => {
      const elapsed = (Date.now() - startTime) / 1000;
      const progress = Math.min(88, Math.round(45 * Math.log10(elapsed + 1)));
      setBatchProgress(progress);
      if (progress < 60) {
        const idx = Math.min(Math.floor(progress / (60 / testCount)), testCount - 1);
        setBatchProgressMessage(`Testing ticket ${idx + 1}/${testCount}: ${tickets[idx]?.subject || '...'}`);
      } else if (progress < 80) {
        setBatchProgressMessage('Evaluating all results...');
      } else {
        setBatchProgressMessage('Generating analytics...');
      }
    }, 2000);

    try {
      const payload = {
        limit: testCount,
        kb_articles: kb_articles || null,
        agent_system_prompt: agent_system_prompt || null,
      };
      if (filterPriority !== 'all') payload.priority = filterPriority;

      const data = await authFetch(`/api/zendesk/batch-test`, {
        method: 'POST',
        body: JSON.stringify(payload),
      });

      clearInterval(progressInterval);
      setBatchProgress(100);
      setBatchProgressMessage('✓ Batch test complete!');

      setTimeout(() => {
        setBatchResults(data);
        setBatchProgress(0);
        setBatchProgressMessage('');
        if (data.results) {
          const perTicket = {};
          data.results.forEach(r => {
            // Try every location the ticket ID might appear in the response shape
            const ticketId =
              r.ticket?.id ||
              r.scenario?.metadata?.ticket_id ||
              r.scenario?.ticket_id ||
              r.scenario?.id;
            if (ticketId) perTicket[ticketId] = r;
            // Also key by string version in case ticket.id is a number
            if (ticketId) perTicket[String(ticketId)] = r;
          });
          setTicketResults(prev => ({ ...prev, ...perTicket }));
        }
      }, 800);
    } catch {
      alert('Failed to run batch test');
      clearInterval(progressInterval);
      setBatchProgress(0);
      setBatchProgressMessage('');
    } finally {
      setTimeout(() => setBatchTesting(false), 800);
    }
  };

  // ── Helpers ───────────────────────────────────────────────────────

  const getPriorityColor = (priority) => ({ urgent: '#ff4444', high: '#ff8c00', normal: '#4CAF50', low: '#2196F3' }[priority] || '#999');
  const getStatusColor = (status) => ({ open: '#ff9800', pending: '#2196F3', solved: '#4CAF50', closed: '#666' }[status] || '#999');
  const isConnected = oauthStatus?.connected === true;

  // ── Derived filter values (client-side from loaded tickets) ───────
  const allTags        = [...new Set(tickets.flatMap(t => t.tags || []))].sort();
  const uniqueStatuses = [...new Set(tickets.map(t => t.status).filter(Boolean))];
  const uniqueChannels = [...new Set(tickets.map(t => t.channel).filter(Boolean))];

  const toggleTag = (tag) => {
    setSelectedTags(prev => {
      const next = new Set(prev);
      next.has(tag) ? next.delete(tag) : next.add(tag);
      return next;
    });
  };

  const clearFilters = () => {
    setFilterPriority('all');
    setFilterStatus('all');
    setFilterChannel('all');
    setSelectedTags(new Set());
  };

  const hasActiveFilters = filterPriority !== 'all' || filterStatus !== 'all' || filterChannel !== 'all' || selectedTags.size > 0;

  // Client-side tag + channel filter applied on top of server-filtered tickets
  const visibleTickets = tickets.filter(t => {
    if (filterChannel !== 'all' && t.channel !== filterChannel) return false;
    if (selectedTags.size > 0) {
      const ticketTags = new Set(t.tags || []);
      for (const tag of selectedTags) {
        if (!ticketTags.has(tag)) return false;
      }
    }
    return true;
  });

  const getBatchButtonText = () => {
    const count = tickets.length;
    if (batchTesting) {
      return (
        <span className="button-with-progress">
          <span className="button-text">⏳ Testing {count} ticket{count !== 1 ? 's' : ''}...</span>
          <div className="button-progress-bar">
            <div className="button-progress-fill" style={{ width: `${batchProgress}%` }} />
          </div>
        </span>
      );
    }
    return `🧪 Batch Test (${count} ${count === 1 ? 'ticket' : 'tickets'})`;
  };

  return (
    <div className={`zendesk-container${theme === 'light' ? ' light-mode' : ''}`}>

      {/* ── Connection Panel ───────────────────────────────────── */}
      <div className={`connection-panel ${isConnected ? 'connection-panel-compact' : ''}`}>
        {!isConnected && <h2>🔗 Zendesk Connection</h2>}
        {!integrationStatus && <p className="status-checking">Checking backend status...</p>}
        {integrationStatus?.status === 'error' && (
          <div className="connection-status error">
            <p>⚠️ Cannot reach backend at {API_BASE}/health</p>
            <p className="hint">Make sure the backend is running: <code>cd backend && python -m app.main</code></p>
          </div>
        )}
        {integrationStatus && !integrationStatus.zendesk_oauth_configured && integrationStatus.status !== 'error' && (
          <div className="connection-status mock-mode">
            <p>📋 <strong>Mock Mode</strong> — Zendesk OAuth not configured</p>
            <p className="hint">Connect your Zendesk account to import real tickets, or use mock tickets to test the platform.</p>
            <button className="btn-configure" onClick={() => setShowConfigModal(true)}>⚙️ Configure Zendesk</button>
          </div>
        )}
        {integrationStatus?.zendesk_oauth_configured && (
          <>
            {isConnected && oauthStatus?.connections?.length > 0 ? (
              <div className="connection-status connected compact">
                {oauthStatus.connections.map(conn => (
                  <div key={conn.id} className="connection-info-inline">
                    <span className="connection-badge">✅ Connected</span>
                    <span className="connection-subdomain">{conn.subdomain}.zendesk.com</span>
                    <span className="connection-date">since {conn.created_at && !isNaN(new Date(conn.created_at)) ? new Date(conn.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'today'}</span>
                    <button className="btn-disconnect" onClick={() => disconnectZendesk(conn.id)}>Disconnect</button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="connection-status not-connected">
                <p>🔌 <strong>Not Connected</strong> — Enter your Zendesk subdomain to connect</p>
                <div className="oauth-connect-form">
                  <div className="subdomain-input-group">
                    <input type="text" value={subdomain} onChange={e => setSubdomain(e.target.value)}
                      placeholder="your-company" className="subdomain-input" disabled={connecting} />
                    <span className="subdomain-suffix">.zendesk.com</span>
                  </div>
                  <button className="btn-connect" onClick={connectZendesk} disabled={connecting || !subdomain.trim()}>
                    {connecting ? '⏳ Connecting...' : '🔗 Connect Zendesk'}
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* ── Shared KB config status banner ────────────────────── */}
      {sharedKbArticles && (
        <div style={{
          margin: '0 0 12px',
          padding: '8px 16px',
          borderRadius: 8,
          background: 'rgba(124,58,237,0.08)',
          border: '1px solid rgba(124,58,237,0.25)',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          fontSize: 13,
          color: '#a78bfa',
        }}>
          <span>📚</span>
          <span>
            <strong>{sharedKbArticles.length} KB article{sharedKbArticles.length !== 1 ? 's' : ''}</strong> active
            {sharedAgentPrompt && <> · Custom agent prompt set</>}
            {' '}— all tests will be KB-grounded and include Groundedness scoring.
          </span>
          <span style={{ marginLeft: 'auto', fontSize: 11, opacity: 0.7 }}>
            Change via 📚 KB in the nav
          </span>
        </div>
      )}

      {/* ── Header ─────────────────────────────────────────────── */}
      <div className="zendesk-header">
        <div className="header-title-row">
          <h1>Zendesk Tickets</h1>
          {tickets.length > 0 && (
            <span className="ticket-count-badge">
              {tickets.length} ticket{tickets.length !== 1 ? 's' : ''}
              {filterPriority !== 'all' && ` (${filterPriority})`}
            </span>
          )}
        </div>
        <div className="header-actions">
          {isConnected ? (
            <button className="btn-import btn-import-real"
              onClick={() => {
                setShowImportFilters(v => {
                  if (!v) setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 80);
                  return !v;
                });
              }}
              disabled={importing}
              style={{ display: 'flex', alignItems: 'center', gap: 6 }}
            >
              {importing ? '⏳ Importing...' : showImportFilters ? '▲ Hide Import Config' : '⚙️ Configure Import'}
            </button>
          ) : (
            <button className="btn-import" onClick={() => importMockTickets(10)} disabled={importing}>
              {importing ? '⏳ Importing...' : '📥 Import 10 Mock Tickets'}
            </button>
          )}
          <button className="btn-batch" onClick={runBatchTest} disabled={batchTesting || tickets.length === 0}>
            {getBatchButtonText()}
          </button>
          {tickets.length > 0 && (
            <button className="btn-clear-tickets" onClick={() => setShowClearConfirm(true)} title="Remove all imported tickets">
              🗑️ Clear
            </button>
          )}
        </div>
      </div>

      {/* ── Import Settings (connected users only) ─────────────── */}
      {isConnected && (
        <div className={`import-settings${showImportFilters ? " import-settings-visible" : ""}`}>
          <div
            className="import-settings-header"
            onClick={() => setShowImportFilters(v => !v)}
            style={{ cursor: 'pointer', userSelect: 'none', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
          >
            <span className="import-settings-title">📋 Import Filters</span>
            <span style={{ fontSize: 12, color: '#94a3b8', fontWeight: 600 }}>
              {showImportFilters ? '▲ Collapse' : '▼ Expand'}
            </span>
          </div>

          <div className="import-settings-grid">
            <div className="import-shared-row">
              <label className="import-field-maxtickets">
                Max tickets:
                <input id="import-max-tickets" type="number" value={importMaxTickets}
                  onChange={e => setImportMaxTickets(Math.min(parseInt(e.target.value) || 50, MAX_TICKETS_LIMIT))}
                  min="1" max={MAX_TICKETS_LIMIT}
                  title={`Maximum ${MAX_TICKETS_LIMIT} tickets per import (Zendesk rate limit)`} />
              </label>
              <label className="import-field-status">
                Status:
                <select id="import-status" value={importStatus} onChange={e => setImportStatus(e.target.value)}>
                  <option value="">All statuses</option>
                  <option value="new">New</option>
                  <option value="open">Open</option>
                  <option value="pending">Pending</option>
                  <option value="hold">Hold</option>
                  <option value="solved">Solved</option>
                  <option value="closed">Closed</option>
                </select>
              </label>
              <label className="import-field-priority">
                Priority:
                <select id="import-priority" value={importPriority} onChange={e => setImportPriority(e.target.value)}>
                  <option value="">All priorities</option>
                  <option value="urgent">Urgent</option>
                  <option value="high">High</option>
                  <option value="normal">Normal</option>
                  <option value="low">Low</option>
                </select>
              </label>
              <label className="import-field-channel">
                Channel:
                <select id="import-channel" value={importChannel} onChange={e => setImportChannel(e.target.value)}>
                  <option value="">All channels</option>
                  <option value="web">Web</option>
                  <option value="email">Email</option>
                  <option value="api">API</option>
                  <option value="chat">Chat</option>
                  <option value="voice">Voice</option>
                  <option value="twitter">Twitter/X</option>
                  <option value="facebook">Facebook</option>
                </select>
              </label>
              <label className="checkbox-label">
                <input id="import-include-comments" name="import-include-comments" type="checkbox" checked={importIncludeComments}
                  onChange={e => setImportIncludeComments(e.target.checked)} />
                Include comments
              </label>
            </div>

            <div className="import-section-divider" />

            <div className="import-mode-section">
              <div className="import-mode-toggle">
                <button
                  className={`import-mode-btn ${importFilterMode === 'date' ? 'active' : ''}`}
                  onClick={() => setImportFilterMode('date')}
                >📅 By Date</button>
                <button
                  className={`import-mode-btn ${importFilterMode === 'id' ? 'active' : ''}`}
                  onClick={() => setImportFilterMode('id')}
                >#️⃣ By Ticket ID</button>
              </div>

              <div className="import-fields-row">
                {importFilterMode === 'date' && (<>
                  <label className="import-field-date">
                    <span className="field-label-text">Start date</span>
                    <div className="date-input-wrapper">
                      <span className="date-icon">📅</span>
                      <input id="import-start-date" type="date" value={importStartDate}
                        onChange={e => setImportStartDate(e.target.value)} />
                    </div>
                  </label>
                  <label className="import-field-date">
                    <span className="field-label-text">End date</span>
                    <div className="date-input-wrapper">
                      <span className="date-icon">📅</span>
                      <input id="import-end-date" type="date" value={importEndDate}
                        onChange={e => setImportEndDate(e.target.value)} />
                    </div>
                  </label>
                  {(() => {
                    const hasDates = importStartDate && importEndDate;
                    const calcDays = hasDates
                      ? Math.max(1, Math.round((new Date(importEndDate) - new Date(importStartDate)) / 86400000))
                      : null;
                    return (
                      <>
                        <div className="import-field-or">{hasDates ? '=' : 'or'}</div>
                        <label className="import-field-daysback">
                          <span className="field-label-text">Days back</span>
                          <input
                            id="import-days-back"
                            type="number"
                            value={hasDates ? calcDays : importDaysBack}
                            onChange={e => !hasDates && setImportDaysBack(parseInt(e.target.value) || 30)}
                            min="1" max="365"
                            readOnly={!!hasDates}
                            className={hasDates ? 'days-back-calculated' : ''}
                            title={hasDates ? `${calcDays} days between selected dates` : 'Import tickets from the last N days'} />
                          <span className="field-hint">
                            {hasDates ? `${calcDays}-day range` : 'used when no dates set'}
                          </span>
                        </label>
                      </>
                    );
                  })()}
                </>)}

                {importFilterMode === 'id' && (<>
                  <label className="import-field-ticketid">
                    <span className="field-label-text">Min ticket ID</span>
                    <input id="import-min-ticket-id" type="number" value={importMinTicketId}
                      onChange={e => setImportMinTicketId(e.target.value)}
                      placeholder="e.g. 1000" min="1" />
                  </label>
                  <div className="import-field-arrow">→</div>
                  <label className="import-field-ticketid">
                    <span className="field-label-text">Max ticket ID</span>
                    <input id="import-max-ticket-id" type="number" value={importMaxTicketId}
                      onChange={e => setImportMaxTicketId(e.target.value)}
                      placeholder="e.g. 2000" min="1" />
                  </label>
                  <span className="field-hint" style={{alignSelf:'flex-end', paddingBottom:8}}>Leave blank for no limit</span>
                </>)}
              </div>
            </div>

            <div className="rate-limit-note">
              ℹ️ Zendesk rate limits: Essential/Team 200 req/min · Professional 400 req/min · Enterprise 700 req/min.
              Importing {importMaxTickets} tickets uses ~{Math.ceil(importMaxTickets / 100) + (importIncludeComments ? importMaxTickets : 0)} API calls.
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', paddingTop: 4 }}>
              <button
                className="btn-import btn-import-real"
                onClick={importRealTickets}
                disabled={importing}
                style={{ minWidth: 160 }}
              >
                {importing ? '⏳ Importing...' : '📥 Run Import'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Import Notification ────────────────────────────────── */}
      {lastImportInfo && (
        <div className="import-notification">
          <span className="import-notification-text">
            ✅ Imported {lastImportInfo.count} ticket{lastImportInfo.count !== 1 ? 's' : ''}
            {lastImportInfo.source !== 'mock' && <> from <strong>{lastImportInfo.source}.zendesk.com</strong></>}
            {lastImportInfo.pii > 0 && <> · {lastImportInfo.pii} PII detection{lastImportInfo.pii !== 1 ? 's' : ''} anonymized</>}
          </span>
          <button className="import-notification-dismiss" onClick={() => setLastImportInfo(null)}>✕</button>
        </div>
      )}

      {/* ── Browse Filters ─────────────────────────────────────── */}
      {tickets.length > 0 && (
        <div style={{ marginBottom: 12 }}>
          {/* Filter controls row */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
            padding: '8px 12px',
            background: '#1a1a2e',
            border: '1px solid #2d2d4e',
            borderRadius: allTags.length > 0 ? '8px 8px 0 0' : 8,
          }}>
            {/* Priority */}
            <select
              value={filterPriority}
              onChange={e => setFilterPriority(e.target.value)}
              style={{ padding: '5px 8px', borderRadius: 6, border: '1px solid #2d2d4e', background: '#0f0f23', color: '#e2e8f0', fontSize: 12 }}
            >
              <option value="all">All priorities</option>
              {['urgent', 'high', 'normal', 'low'].map(p => <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>)}
            </select>

            {/* Status */}
            {uniqueStatuses.length > 0 && (
              <select
                value={filterStatus}
                onChange={e => setFilterStatus(e.target.value)}
                style={{ padding: '5px 8px', borderRadius: 6, border: '1px solid #2d2d4e', background: '#0f0f23', color: '#e2e8f0', fontSize: 12 }}
              >
                <option value="all">All statuses</option>
                {uniqueStatuses.map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
              </select>
            )}

            {/* Channel */}
            {uniqueChannels.length > 0 && (
              <select
                value={filterChannel}
                onChange={e => setFilterChannel(e.target.value)}
                style={{ padding: '5px 8px', borderRadius: 6, border: '1px solid #2d2d4e', background: '#0f0f23', color: '#e2e8f0', fontSize: 12 }}
              >
                <option value="all">All channels</option>
                {uniqueChannels.map(c => <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>)}
              </select>
            )}

            {/* Divider */}
            {(uniqueStatuses.length > 0 || uniqueChannels.length > 0) && (
              <div style={{ width: 1, height: 18, background: '#2d2d4e', flexShrink: 0 }} />
            )}

            {/* Clear */}
            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                style={{ padding: '4px 10px', borderRadius: 20, fontSize: 11, cursor: 'pointer', border: '1px solid rgba(239,68,68,0.3)', background: 'transparent', color: '#ef4444' }}
              >
                ✕ Clear
              </button>
            )}

            {/* Count */}
            <span style={{ marginLeft: 'auto', fontSize: 12, color: '#6b7280' }}>
              {visibleTickets.length}{visibleTickets.length !== tickets.length ? ` of ${tickets.length}` : ''} ticket{visibleTickets.length !== 1 ? 's' : ''}
            </span>
          </div>

          {/* Tag multi-select row */}
          {allTags.length > 0 && (
            <div style={{
              padding: '8px 12px',
              background: '#161625',
              border: '1px solid #2d2d4e',
              borderTop: 'none',
              borderRadius: '0 0 8px 8px',
              display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap',
            }}>
              <span style={{ fontSize: 11, color: '#6b7280', fontWeight: 600, flexShrink: 0 }}>
                TAGS{selectedTags.size > 0 && <span style={{ color: '#a78bfa' }}> ({selectedTags.size})</span>}
              </span>
              {allTags.map(tag => {
                const active = selectedTags.has(tag);
                return (
                  <button
                    key={tag}
                    onClick={() => toggleTag(tag)}
                    style={{
                      padding: '2px 10px', borderRadius: 20, fontSize: 11, fontWeight: 500,
                      cursor: 'pointer',
                      border: `1px solid ${active ? '#7c3aed' : 'rgba(124,58,237,0.25)'}`,
                      background: active ? 'rgba(124,58,237,0.2)' : 'transparent',
                      color: active ? '#a78bfa' : '#64748b',
                      transition: 'all 0.12s',
                    }}
                  >
                    {tag}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ── Batch Progress ─────────────────────────────────────── */}
      {batchTesting && (
        <div className="zd-progress-container">
          <div className="zd-progress-header">
            <span className="zd-progress-text">{batchProgressMessage}</span>
            <span className="zd-progress-percentage">{batchProgress}%</span>
          </div>
          <div className="zd-progress-bar">
            <div className="zd-progress-fill" style={{ width: `${batchProgress}%` }}></div>
          </div>
          <div className="zd-progress-info">
            <span>⏱️ Estimated time: ~{tickets.length * 5}-{tickets.length * 8} seconds</span>
            <span>🤖 Testing tickets with AI evaluation</span>
          </div>
          <div className="zd-progress-steps">
            {[['Running Tests', 5, 60], ['Evaluating', 60, 80], ['Analytics', 80, 88], ['Done', 88, 100]].map(([label, start, end], i) => (
              <div key={label} className={`zd-step ${batchProgress >= start ? 'active' : ''} ${batchProgress >= end ? 'complete' : ''}`}>
                <div className="zd-step-icon">{i + 1}</div>
                <div className="zd-step-label">{label}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Batch Results Summary ──────────────────────────────── */}
      {batchResults && (() => {
        const allResults = batchResults.results || [];
        const filteredResults = filterPriority === 'all' ? allResults
          : allResults.filter(r => (r.scenario?.priority || r.ticket?.priority || '').toLowerCase() === filterPriority);
        const successResults = filteredResults.filter(r => r.status === 'success');
        const evaluations = successResults.map(r => r.evaluation?.evaluation || r.evaluation || {});
        const totalTests = filteredResults.length;
        const passedTests = evaluations.filter(e => e.passed).length;
        const failedTests = totalTests - passedTests;
        const passRate = totalTests > 0 ? Math.round((passedTests / totalTests) * 100) : 0;
        const calcAvg = m => evaluations.length === 0 ? 0 : evaluations.reduce((s, e) => s + (e[m] || 0), 0) / evaluations.length;
        const avgScores = {
          accuracy:     calcAvg('accuracy'),
          tone:         calcAvg('tone'),
          resolution:   calcAvg('resolution'),
          efficiency:   calcAvg('efficiency'),
          groundedness: calcAvg('groundedness'),
        };
        const avgScore = evaluations.length > 0 ? evaluations.reduce((s, e) => s + (e.overall_score || 0), 0) / evaluations.length : 0;
        const isFiltered = filterPriority !== 'all';
        const isKbGrounded = batchResults?.summary?.kb_grounded;
        const pieData = [{ name: 'Passed', value: passedTests, color: '#10b981' }, { name: 'Failed', value: failedTests, color: '#ef4444' }];
        const radarData = [
          { metric: 'Accuracy',   value: Number(avgScores.accuracy.toFixed(1)),   fullMark: 5 },
          { metric: 'Tone',       value: Number(avgScores.tone.toFixed(1)),        fullMark: 5 },
          { metric: 'Resolution', value: Number(avgScores.resolution.toFixed(1)), fullMark: 5 },
          { metric: 'Efficiency', value: Number(avgScores.efficiency.toFixed(1)), fullMark: 5 },
          ...(isKbGrounded && avgScores.groundedness != null ? [{
            metric: 'Groundedness',
            value: Number(avgScores.groundedness.toFixed(1)),
            fullMark: 5,
          }] : []),
        ];
        const exportToCSV = () => {
          const headers = ['Ticket', 'Subject', 'Priority', 'Passed', 'Score', 'Accuracy', 'Tone', 'Resolution', 'Efficiency', ...(isKbGrounded ? ['Groundedness'] : [])];
          const rows = filteredResults.map(r => {
            const ev = r.evaluation?.evaluation || r.evaluation || {};
            const ticket = r.ticket || {};
            return [
              `#${ticket.id || ''}`,
              `"${(ticket.subject || r.scenario?.name || '').replace(/"/g, '""')}"`,
              r.scenario?.priority || ticket.priority || '',
              ev.passed ? 'Yes' : 'No',
              `${ev.overall_score || 0}%`,
              (ev.accuracy || 0) / 5,
              (ev.tone || 0) / 5,
              (ev.resolution || 0) / 5,
              (ev.efficiency || 0) / 5,
              ...(isKbGrounded ? [(ev.groundedness || 0) / 5] : []),
            ];
          });
          const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
          const a = Object.assign(document.createElement('a'), {
            href: URL.createObjectURL(new Blob([csv], { type: 'text/csv' })),
            download: `zendesk-batch-results-${isFiltered ? filterPriority + '-' : ''}${Date.now()}.csv`,
          });
          a.click();
        };
        return (
          <div className="zd-batch-results">
            <div className="zd-batch-header">
              <div>
                <h3>Batch Test Results</h3>
                {isFiltered && <span className="zd-filter-indicator">Filtered: {totalTests} of {allResults.length} tickets ({filterPriority} priority)</span>}
                {isKbGrounded && (
                  <span className="badge-kb-grounded" style={{ marginLeft: 10 }}>
                    📚 KB-grounded · Groundedness scored
                  </span>
                )}
              </div>
              <button onClick={exportToCSV} className="btn-export"><Download size={16} />Export to CSV</button>
            </div>
            <div className="zd-stats-grid">
              {[['TOTAL TESTS', totalTests], ['PASSED', passedTests], ['FAILED', failedTests], ['PASS RATE', `${passRate}%`], ['AVG SCORE', `${Math.round(avgScore)}%`]].map(([label, val]) => (
                <div key={label} className="zd-stat-box"><div className="zd-stat-label">{label}</div><div className="zd-stat-value">{val}</div></div>
              ))}
            </div>
            <div className="zd-charts-container">
              <div className="zd-chart-box">
                <h4>Pass/Fail Distribution</h4>
                <ResponsiveContainer width="100%" height={250}>
                  <PieChart>
                    <Pie data={pieData} cx="50%" cy="50%" outerRadius={80} dataKey="value"
                      label={({ cx, cy, midAngle, outerRadius, name, value }) => {
                        const [x, y] = [cx + (outerRadius + 20) * Math.cos(-midAngle * Math.PI / 180), cy + (outerRadius + 20) * Math.sin(-midAngle * Math.PI / 180)];
                        return <text x={x} y={y} fill={theme === 'light' ? '#475569' : '#94a3b8'} textAnchor={x > cx ? 'start' : 'end'} dominantBaseline="central" fontSize={13}>{`${name}: ${value}`}</text>;
                      }}>
                      {pieData.map((e, i) => <Cell key={i} fill={e.color} />)}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="zd-chart-box">
                <h4>Performance Profile{isKbGrounded ? ' (incl. Groundedness)' : ''}</h4>
                <ResponsiveContainer width="100%" height={280}>
                  <RadarChart data={radarData} cx="50%" cy="50%" outerRadius="70%">
                    <PolarGrid stroke={theme === 'light' ? '#e2e8f0' : '#334155'} />
                    <PolarAngleAxis dataKey="metric" tick={{ fill: theme === 'light' ? '#475569' : '#94a3b8', fontSize: 13 }} />
                    <PolarRadiusAxis angle={90} domain={[0, 5]} tick={{ fill: theme === 'light' ? '#475569' : '#94a3b8', fontSize: 11 }} />
                    <Radar name="Score" dataKey="value" stroke="#7c3aed" fill="#7c3aed" fillOpacity={0.3} strokeWidth={2} />
                    <Tooltip formatter={v => [`${v}/5`, 'Score']} />
                  </RadarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── Ticket Grid ────────────────────────────────────────── */}
      {loading ? (
        <div className="loading">Loading tickets...</div>
      ) : visibleTickets.length === 0 ? (
        <div className="empty-state">
          <h3>No tickets yet</h3>
          <p>{hasActiveFilters ? 'No tickets match the current filters.' : filterPriority !== 'all' ? `No ${filterPriority} priority tickets found.` : isConnected ? 'Import tickets from your Zendesk account to start testing.' : 'Import mock tickets to explore the platform, or connect Zendesk for real data.'}</p>
          {hasActiveFilters ? (
            <button className="btn-import-large" onClick={clearFilters} style={{ background: 'transparent', border: '1px solid #374151', color: '#94a3b8' }}>Clear filters</button>
          ) : isConnected ? (
            <button className="btn-import-large" onClick={() => {
              setShowImportFilters(true);
              setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 80);
            }}>⚙️ Configure & Import</button>
          ) : (
            <div className="empty-state-actions">
              <button className="btn-import-large" onClick={() => importMockTickets(10)}>📥 Import 10 Test Tickets</button>
              <button className="btn-import-large" onClick={() => setShowConfigModal(true)}>🔗 Connect Zendesk</button>
            </div>
          )}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {visibleTickets.map(ticket => {
            const result = ticketResults[ticket.id] || ticketResults[String(ticket.id)];
            const ev = result?.evaluation?.evaluation || result?.evaluation || null;
            const isExpanded = expandedTickets[ticket.id] || false;
            const hasResult = !!ev;
            const wasKbGrounded = result?.kb_grounded || ev?.kb_grounded || false;
            const passed = hasResult && ev.passed;
            const toggleExpand = (e) => {
              e.stopPropagation();
              setExpandedTickets(prev => ({ ...prev, [ticket.id]: !prev[ticket.id] }));
            };
            return (
              <div key={ticket.id} style={{
                background: '#1a1a2e',
                border: `1px solid ${hasResult ? (passed ? 'rgba(16,185,129,0.35)' : 'rgba(239,68,68,0.35)') : '#2d2d4e'}`,
                borderRadius: 8,
                padding: '14px 16px',
                transition: 'border-color 0.15s',
              }}
                onMouseEnter={e => { if (!hasResult) e.currentTarget.style.borderColor = '#7c3aed'; }}
                onMouseLeave={e => { if (!hasResult) e.currentTarget.style.borderColor = '#2d2d4e'; }}
              >
                {/* Row 1: ID, badges, result */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 5 }}>
                  <span style={{ fontSize: 11, color: '#6b7280', fontFamily: 'monospace' }}>#{ticket.zendesk_ticket_id || ticket.id}</span>
                  <span style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', color: getPriorityColor(ticket.priority), background: `${getPriorityColor(ticket.priority)}18`, padding: '1px 7px', borderRadius: 10, border: `1px solid ${getPriorityColor(ticket.priority)}40` }}>{ticket.priority}</span>
                  <span style={{ fontSize: 10, padding: '1px 7px', borderRadius: 10, background: `${getStatusColor(ticket.status)}18`, color: getStatusColor(ticket.status), border: `1px solid ${getStatusColor(ticket.status)}40` }}>{ticket.status}</span>
                  {ticket.channel && <span style={{ fontSize: 10, color: '#6b7280' }}>📡 {ticket.channel}</span>}
                  {hasResult && (
                    <>
                      <span style={{ fontSize: 10, fontWeight: 700, color: passed ? '#10b981' : '#ef4444', background: passed ? 'rgba(16,185,129,0.1)' : 'rgba(239,68,68,0.1)', padding: '1px 7px', borderRadius: 10, border: `1px solid ${passed ? '#10b981' : '#ef4444'}40` }}>
                        {passed ? '✓ PASSED' : '✗ FAILED'}
                      </span>
                      <span style={{ fontSize: 10, color: '#94a3b8' }}>{ev.overall_score}%</span>
                      {wasKbGrounded && <span style={{ fontSize: 10, color: '#a78bfa' }}>📚 KB</span>}
                    </>
                  )}
                </div>

                {/* Row 2: Subject */}
                <div style={{ fontSize: 14, fontWeight: 600, color: '#e2e8f0', marginBottom: 4 }}>
                  {ticket.subject}
                </div>

                {/* Row 3: Description preview */}
                {ticket.description && (
                  <div style={{ fontSize: 12, color: '#94a3b8', lineHeight: 1.5, marginBottom: 6 }}>
                    {ticket.description.substring(0, 150)}{ticket.description.length > 150 ? '...' : ''}
                  </div>
                )}
                {/* Row 4: Tags + PII + actions */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  {ticket.tags?.slice(0, 3).map((tag, i) => (
                    <span key={i} style={{ fontSize: 10, padding: '1px 7px', borderRadius: 10, background: 'rgba(124,58,237,0.1)', color: '#a78bfa', border: '1px solid rgba(124,58,237,0.2)' }}>{tag}</span>
                  ))}
                  {ticket.tags?.length > 3 && (
                    <span
                      title={ticket.tags.slice(3).join(', ')}
                      style={{
                        fontSize: 10, padding: '1px 7px', borderRadius: 10,
                        background: 'rgba(107,114,128,0.12)', color: '#6b7280',
                        border: '1px solid rgba(107,114,128,0.2)',
                        cursor: 'default',
                      }}
                    >
                      +{ticket.tags.length - 3} more
                    </span>
                  )}
                  {ticket.pii_detected?.total_detections > 0 && (
                    <span style={{ fontSize: 10, color: '#10b981' }}>🔒 {ticket.pii_detected.total_detections} PII anonymized</span>
                  )}
                  <div style={{ marginLeft: 'auto', display: 'flex', gap: 6, flexShrink: 0 }}>
                    {/* Expand button — always available */}
                    <button
                      onClick={toggleExpand}
                      title={isExpanded ? 'Collapse' : 'Expand ticket'}
                      style={{
                        background: 'transparent',
                        border: '1px solid #374151',
                        color: isExpanded ? '#7c3aed' : '#6b7280',
                        borderRadius: 6, padding: '5px 10px', fontSize: 13,
                        cursor: 'pointer', transition: 'all 0.15s',
                      }}
                      onMouseEnter={e => { e.currentTarget.style.borderColor = '#7c3aed'; e.currentTarget.style.color = '#7c3aed'; }}
                      onMouseLeave={e => { e.currentTarget.style.borderColor = isExpanded ? '#7c3aed' : '#374151'; e.currentTarget.style.color = isExpanded ? '#7c3aed' : '#6b7280'; }}
                    >
                      {isExpanded ? '▲' : '▼'}
                    </button>
                    {/* Test button */}
                    <button
                      onClick={() => testSingleTicket(ticket.id)}
                      disabled={testingTicket === ticket.id}
                      style={{
                        background: hasResult ? '#1f1f3a' : '#7c3aed',
                        color: '#fff', border: hasResult ? '1px solid #374151' : 'none',
                        borderRadius: 6, padding: '5px 14px', fontSize: 12, fontWeight: 600,
                        cursor: testingTicket === ticket.id ? 'not-allowed' : 'pointer',
                      }}
                    >
                      {testingTicket === ticket.id ? '⏳ Testing...' : hasResult ? '🔄 Retest' : '🧪 Test This Ticket'}
                    </button>
                  </div>
                </div>
                {/* Expanded section — always available */}
                {isExpanded && (
                  <div style={{
                    marginTop: 12, paddingTop: 12,
                    borderTop: '1px solid #2d2d4e',
                  }}>
                    {/* Full description */}
                    {ticket.description && (
                      <div style={{ marginBottom: 14 }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', letterSpacing: '0.06em', marginBottom: 6 }}>DESCRIPTION</div>
                        <div style={{ fontSize: 13, color: '#94a3b8', lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>
                          {ticket.description}
                        </div>
                      </div>
                    )}

                    {/* Comment thread */}
                    {ticket.comments?.length > 0 && (
                      <div style={{ marginBottom: 14 }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', letterSpacing: '0.06em', marginBottom: 8 }}>
                          CONVERSATION THREAD ({ticket.comments.length} comment{ticket.comments.length !== 1 ? 's' : ''})
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                          {ticket.comments.map((comment, i) => {
                            const isAgent = comment.author_type === 'agent' || comment.role === 'agent';
                            return (
                              <div key={i} style={{
                                display: 'flex', flexDirection: 'column',
                                alignItems: isAgent ? 'flex-end' : 'flex-start',
                              }}>
                                <div style={{ fontSize: 10, color: '#6b7280', marginBottom: 2 }}>
                                  {isAgent ? '🧑‍💼 Agent' : '👤 Customer'}
                                  {comment.author && <span style={{ marginLeft: 4 }}>— {comment.author}</span>}
                                </div>
                                <div style={{
                                  maxWidth: '85%', padding: '8px 12px', fontSize: 12,
                                  color: '#e2e8f0', lineHeight: 1.6, whiteSpace: 'pre-wrap',
                                  borderRadius: isAgent ? '10px 4px 10px 10px' : '4px 10px 10px 10px',
                                  background: isAgent ? 'rgba(56,189,248,0.08)' : '#0f0f23',
                                  border: `1px solid ${isAgent ? 'rgba(56,189,248,0.2)' : '#2d2d4e'}`,
                                }}>
                                  {comment.content || comment.body || comment.text || ''}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Test results — only when available */}
                    {hasResult && (
                      <div style={{ borderTop: '1px solid #2d2d4e', paddingTop: 12 }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', letterSpacing: '0.06em', marginBottom: 10 }}>
                          TEST RESULTS
                        </div>
                        <div className="expanded-overall-score">Overall Score: <strong>{ev.overall_score}%</strong></div>
                        <div className="expanded-metrics-grid">
                          {['accuracy', 'tone', 'resolution', 'efficiency'].map(metric => {
                            const val = ev[metric] || 0;
                            const color = val >= 4 ? '#10b981' : val >= 3 ? '#f59e0b' : '#ef4444';
                            return (
                              <div key={metric} className="expanded-metric-tile">
                                <div className="metric-tile-label">{metric.charAt(0).toUpperCase() + metric.slice(1)}</div>
                                <div className="metric-tile-score" style={{ color }}>{val}/5</div>
                                {ev[`${metric}_reasoning`] && (
                                  <div className="metric-tile-reasoning">{ev[`${metric}_reasoning`]}</div>
                                )}
                              </div>
                            );
                          })}
                          {ev.groundedness !== undefined && (
                            <div className="expanded-metric-tile groundedness-tile">
                              <div className="metric-tile-label">Groundedness</div>
                              <div className="metric-tile-score" style={{ color: ev.groundedness >= 4 ? '#a78bfa' : ev.groundedness >= 3 ? '#f59e0b' : '#ef4444' }}>
                                {ev.groundedness}/5
                              </div>
                              <span className={`groundedness-label ${ev.groundedness >= 4 ? 'grounded' : ev.groundedness >= 3 ? 'partial' : 'hallucinated'}`}>
                                {ev.groundedness >= 4 ? 'Based on KB' : ev.groundedness >= 3 ? 'Partial' : 'Hallucinated'}
                              </span>
                              {ev.groundedness_reasoning && (
                                <div className="metric-tile-reasoning">{ev.groundedness_reasoning}</div>
                              )}
                            </div>
                          )}
                        </div>
                        {ev.hallucination_detected && (
                          <div className="hallucination-alert">
                            <strong>⚠️ Hallucination detected</strong> — the agent provided information not found in the knowledge base.
                            {ev.hallucination_examples?.length > 0 && (
                              <ul className="hallucination-examples">
                                {ev.hallucination_examples.map((ex, i) => <li key={i}>{ex}</li>)}
                              </ul>
                            )}
                          </div>
                        )}
                        {result?.simulation?.turns && (
                          <div className="expanded-section">
                            <h4>💬 AI Conversation</h4>
                            <div className="conversation-transcript">
                              {result.simulation.turns.map((turn, i) => (
                                <div key={i} className={`transcript-turn ${turn.role}`}>
                                  <span className="turn-role">{turn.role === 'customer' ? '👤 Customer' : '🤖 Agent'}</span>
                                  <p className="turn-message">{turn.message || turn.content}</p>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        <div className="expanded-insights">
                          {ev.strengths?.length > 0 && <div className="insight-column"><h4>💪 Strengths</h4><ul>{ev.strengths.map((s, i) => <li key={i}>{s}</li>)}</ul></div>}
                          {ev.improvements?.length > 0 && <div className="insight-column"><h4>🎯 Areas for Improvement</h4><ul>{ev.improvements.map((s, i) => <li key={i}>{s}</li>)}</ul></div>}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ── Modals ────────────────────────────────────────────── */}

      {testResults && (
        <TestResultsModal
          results={testResults}
          onClose={() => setTestResults(null)}
          theme={theme}
        />
      )}

      <RunTestModal
        isOpen={runTestModalOpen}
        onClose={() => {
          setRunTestModalOpen(false);
          setPendingTestTicketId(null);
        }}
        onConfirm={(config) => {
          if (runTestModalMode === 'single' && pendingTestTicketId) {
            executeTicketTest(pendingTestTicketId, config);
          } else {
            executeBatchTest(config);
          }
        }}
        mode={runTestModalMode}
        ticketSubject={
          runTestModalMode === 'single'
            ? tickets.find(t => t.id === pendingTestTicketId)?.subject || ''
            : `${tickets.length} ticket${tickets.length !== 1 ? 's' : ''}`
        }
      />

      {/* ── Config Modal ───────────────────────────────────────── */}
      {showConfigModal && (
        <div className="modal-overlay" onClick={() => { setShowConfigModal(false); setConfigStep(1); setConfigError(''); }}>
          <div className="modal-content config-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 560 }}>
            <div className="modal-header">
              <h2>🔗 Connect to Zendesk</h2>
              <button className="btn-close" onClick={() => { setShowConfigModal(false); setConfigStep(1); setConfigError(''); }}>×</button>
            </div>

            {configStep === 1 ? (
              /* ── Step 1: Setup instructions ── */
              <div className="modal-body" style={{ padding: '24px 30px' }}>
                <p style={{ marginBottom: 16, color: 'var(--text-secondary)', fontSize: 14 }}>
                  To connect your Zendesk account, you'll need to create an OAuth client in your Zendesk Admin Center. This takes about 2 minutes.
                </p>

                <div style={{ background: 'rgba(124,58,237,0.07)', border: '1px solid rgba(124,58,237,0.2)', borderRadius: 8, padding: '16px 20px', marginBottom: 20 }}>
                  <p style={{ fontWeight: 600, marginBottom: 12, fontSize: 13 }}>📋 Step-by-step setup:</p>
                  <ol style={{ paddingLeft: 20, lineHeight: 2, fontSize: 13, color: 'var(--text-secondary)', margin: 0 }}>
                    <li>Go to your Zendesk Admin Center</li>
                    <li>Navigate to <strong>Apps and integrations → APIs → OAuth clients</strong></li>
                    <li>Click <strong>Add OAuth client</strong></li>
                    <li>Fill in:
                      <ul style={{ paddingLeft: 18, marginTop: 4 }}>
                        <li><strong>Client name:</strong> Test My Agent</li>
                        <li><strong>Client kind:</strong> Confidential</li>
                        <li><strong>Redirect URL:</strong> <code style={{ fontSize: 11, background: 'rgba(0,0,0,0.2)', padding: '1px 5px', borderRadius: 3 }}>https://app.testmyagent.io/api/zendesk/oauth/callback</code></li>
                      </ul>
                    </li>
                    <li>Save — Zendesk will show you a <strong>Client ID</strong> and <strong>Secret</strong></li>
                    <li>Copy both and click <strong>Next</strong> below</li>
                  </ol>
                </div>

                <a
                  href="https://support.zendesk.com/hc/en-us/articles/4408845965210"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ fontSize: 13, color: '#a78bfa', display: 'block', marginBottom: 20 }}
                >
                  📖 Zendesk OAuth client documentation ↗
                </a>

                <div className="config-actions">
                  <button className="btn-cancel" onClick={() => { setShowConfigModal(false); setConfigStep(1); }}>Cancel</button>
                  <button className="btn-save-config" onClick={() => setConfigStep(2)}>
                    Next: Enter Credentials →
                  </button>
                </div>
              </div>
            ) : (
              /* ── Step 2: Credential entry ── */
              <div className="modal-body" style={{ padding: '24px 30px' }}>
                <button
                  onClick={() => { setConfigStep(1); setConfigError(''); }}
                  style={{ background: 'none', border: 'none', color: '#a78bfa', cursor: 'pointer', fontSize: 13, padding: 0, marginBottom: 16 }}
                >
                  ← Back to instructions
                </button>

                <div className="config-field" style={{ marginBottom: 16 }}>
                  <label style={{ display: 'block', fontSize: 11, fontWeight: 600, letterSpacing: 1, color: 'var(--text-secondary)', marginBottom: 6 }}>ZENDESK SUBDOMAIN</label>
                  <div className="config-subdomain-group">
                    <input
                      type="text"
                      value={configSubdomain}
                      onChange={e => setConfigSubdomain(e.target.value)}
                      placeholder="your-company"
                      disabled={configSaving}
                    />
                    <span className="config-subdomain-suffix">.zendesk.com</span>
                  </div>
                </div>

                <div className="config-field" style={{ marginBottom: 16 }}>
                  <label style={{ display: 'block', fontSize: 11, fontWeight: 600, letterSpacing: 1, color: 'var(--text-secondary)', marginBottom: 6 }}>CLIENT ID</label>
                  <input
                    type="text"
                    value={configClientId}
                    onChange={e => setConfigClientId(e.target.value)}
                    placeholder="e.g. 46331980876052"
                    disabled={configSaving}
                    style={{ width: '100%', boxSizing: 'border-box' }}
                  />
                </div>

                <div className="config-field" style={{ marginBottom: 16 }}>
                  <label style={{ display: 'block', fontSize: 11, fontWeight: 600, letterSpacing: 1, color: 'var(--text-secondary)', marginBottom: 6 }}>CLIENT SECRET</label>
                  <input
                    type="password"
                    value={configClientSecret}
                    onChange={e => setConfigClientSecret(e.target.value)}
                    placeholder="Paste your OAuth client secret"
                    disabled={configSaving}
                    onKeyDown={e => e.key === 'Enter' && saveZendeskCredentials()}
                    style={{ width: '100%', boxSizing: 'border-box' }}
                  />
                  <p style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 6, opacity: 0.7 }}>
                    Your secret is stored encrypted and isolated to your workspace. Need help? Email fernando@cxperiences.com
                  </p>
                </div>

                {configError && <div className="config-error">{configError}</div>}

                <div className="config-actions">
                  <button className="btn-cancel" onClick={() => { setShowConfigModal(false); setConfigStep(1); setConfigError(''); }} disabled={configSaving}>Cancel</button>
                  <button
                    className="btn-save-config"
                    onClick={saveZendeskCredentials}
                    disabled={configSaving || !configSubdomain.trim() || !configClientId.trim() || !configClientSecret.trim()}
                  >
                    {configSaving ? '⏳ Connecting...' : '🔗 Connect to Zendesk'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Clear Confirmation Modal ───────────────────────────── */}
      {showClearConfirm && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1001,
        }}
          onClick={() => setShowClearConfirm(false)}
        >
          <div style={{
            background: '#1a1a2e', border: '1px solid #2d2d4e', borderRadius: 12,
            padding: 28, width: 400, maxWidth: '95vw',
          }}
            onClick={e => e.stopPropagation()}
          >
            <h3 style={{ color: '#e2e8f0', fontWeight: 700, fontSize: 16, marginTop: 0, marginBottom: 10 }}>
              Clear all tickets?
            </h3>
            <p style={{ color: '#94a3b8', fontSize: 13, marginBottom: 24, lineHeight: 1.6 }}>
              All <strong style={{ color: '#e2e8f0' }}>{tickets.length} imported ticket{tickets.length !== 1 ? 's' : ''}</strong> and their test results will be permanently removed. This cannot be undone.
            </p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowClearConfirm(false)}
                style={{
                  background: 'transparent', border: '1px solid #374151',
                  color: '#94a3b8', borderRadius: 6, padding: '8px 16px',
                  fontSize: 13, cursor: 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                onClick={clearAllTickets}
                style={{
                  background: '#ef4444', color: '#fff', border: 'none',
                  borderRadius: 6, padding: '8px 18px', fontSize: 13,
                  fontWeight: 600, cursor: 'pointer',
                }}
              >
                🗑️ Clear all tickets
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ── Sub-components ────────────────────────────────────────────────────────────

const ScoreCard = ({ label, score, max, reasoning, className }) => {
  const pct = (score / max) * 100;
  const color = pct >= 80 ? '#4CAF50' : pct >= 60 ? '#ff9800' : '#ff4444';
  // Groundedness uses purple accent
  const isGroundedness = label === 'Groundedness';
  const displayColor = isGroundedness
    ? (score >= 4 ? '#a78bfa' : score >= 3 ? '#f59e0b' : '#ef4444')
    : color;
  return (
    <div className={`score-card${className ? ' ' + className : ''}`}>
      <div className="score-card-header">
        <span className="score-card-label">{label}</span>
        <span className="score-card-value" style={{ color: displayColor }}>{score}/{max}</span>
      </div>
      <div className="score-card-bar">
        <div className="score-card-fill" style={{ width: `${pct}%`, backgroundColor: displayColor }} />
      </div>
      {reasoning && <p className="score-reasoning">{reasoning}</p>}
    </div>
  );
};

const TestResultsModal = ({ results, onClose, theme }) => {
  const evaluation = results.evaluation?.evaluation || {};
  const simulation = results.simulation || {};
  const ticket = results.ticket || {};
  const isKbGrounded = results.kb_grounded || evaluation.kb_grounded || false;
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Test Results — {ticket.subject}</h2>
          <button className="btn-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          <div className={`result-status ${evaluation.passed ? 'passed' : 'failed'}`}>
            {evaluation.passed ? '✓ Test Passed' : '✗ Test Failed'}
            {isKbGrounded && <span className="badge-kb-grounded" style={{ marginLeft: 10 }}>📚 KB-grounded</span>}
          </div>
          <div className="scores-section">
            <h3>Evaluation Scores</h3>
            <div className="score-grid">
              {['accuracy', 'tone', 'resolution', 'efficiency'].map(m => (
                <ScoreCard
                  key={m}
                  label={m.charAt(0).toUpperCase() + m.slice(1)}
                  score={evaluation[m]}
                  max={5}
                  reasoning={evaluation[`${m}_reasoning`]}
                />
              ))}
              {evaluation.groundedness !== undefined && (
                <ScoreCard
                  key="groundedness"
                  label="Groundedness"
                  score={evaluation.groundedness}
                  max={5}
                  reasoning={evaluation.groundedness_reasoning}
                  className="groundedness-card"
                />
              )}
            </div>

            {evaluation.hallucination_detected && (
              <div className="hallucination-alert" style={{ marginTop: 12 }}>
                <strong>⚠️ Hallucination detected</strong> — the agent provided information not found in the knowledge base.
                {evaluation.hallucination_examples?.length > 0 && (
                  <ul className="hallucination-examples">
                    {evaluation.hallucination_examples.map((ex, i) => <li key={i}>{ex}</li>)}
                  </ul>
                )}
              </div>
            )}

            <div className="overall-score">
              <span>Overall Score:</span>
              <span className="score-value-large">{evaluation.overall_score}/100</span>
            </div>
          </div>
          <div className="conversation-section">
            <h3>Conversation ({simulation.turns?.length || 0} turns)</h3>
            <div className="conversation-turns">
              {simulation.turns?.map((turn, idx) => (
                <div key={idx} className={`turn turn-${turn.role}`}>
                  <div className="turn-role">{turn.role === 'customer' ? '👤 Customer' : '🤖 Agent'}</div>
                  <div className="turn-content">{turn.content}</div>
                </div>
              ))}
            </div>
            <div className="conversation-outcome">
              <strong>Outcome:</strong> {simulation.outcome}{' | '}
              <strong>Containment:</strong> {simulation.containment ? '✓ Yes' : '✗ No'}
              {simulation.kb_grounded && <>{' | '}<strong>KB-grounded:</strong> ✓ Yes</>}
            </div>
          </div>
          {evaluation.strengths?.length > 0 && (
            <div className="feedback-section">
              <h4>✓ Strengths</h4>
              <ul>{evaluation.strengths.map((s, i) => <li key={i}>{s}</li>)}</ul>
            </div>
          )}
          {evaluation.improvements?.length > 0 && (
            <div className="feedback-section">
              <h4>→ Areas for Improvement</h4>
              <ul>{evaluation.improvements.map((s, i) => <li key={i}>{s}</li>)}</ul>
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn-primary" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
};

export default ZendeskTickets;
